import './lib/zod-config';
import './styles.css';
import './i18n';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';
import { relayOAuthPopup } from '@/components/mcp/oauth';
import { setUnauthorizedHandler } from '@/lib/api';
import { setAuthAdapter } from '@/lib/auth';
import { createQueryClient, qk } from '@/lib/queries';
import { applyStoredTheme, useTheme } from '@/lib/theme';
import { IS_MOCK } from '@/lib/utils';
import { createAppRouter } from './router';

applyStoredTheme();

function ThemedToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="bottom-right"
      toastOptions={{
        style: {
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-pop)',
          color: 'var(--fg)',
          borderRadius: '14px',
          fontFamily: 'var(--font-sans)',
          fontSize: '13px',
        },
      }}
    />
  );
}

async function boot() {
  // MCP sign-in popup landing: hand the result to the app window and close (no app needed).
  if (relayOAuthPopup()) {
    document.getElementById('root')!.textContent = 'Signed in. You can close this window.';
    return;
  }
  // Literal env check (not IS_MOCK) so production builds dead-code-eliminate the mock chunk.
  if (import.meta.env.VITE_MOCK_API === '1') {
    const mock = await import('./mock');
    mock.installMockFetch();
    setAuthAdapter(mock.mockAuth);
  } else {
    const { betterAuthAdapter } = await import('./lib/auth/better-auth');
    setAuthAdapter(betterAuthAdapter);
  }

  const queryClient = createQueryClient();
  const router = createAppRouter(queryClient);

  setUnauthorizedHandler(() => {
    queryClient.setQueryData(qk.session, null);
    if (!location.pathname.startsWith('/login') && !location.pathname.startsWith('/setup')) {
      void router.navigate({ to: '/login', search: { redirect: location.pathname } });
    }
  });

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ThemedToaster />
      </QueryClientProvider>
    </StrictMode>,
  );

  // PWA: register the service worker (web push lands in phase 2). Skip in dev/mock.
  if (import.meta.env.PROD && !IS_MOCK && 'serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  }
}

void boot();
